/** @file This files contains helpers for working with either asyncs. */
import { duration } from 'moment';
import { Maybe, MaybeAsync } from 'purify-ts';
import { Left, Right } from 'purify-ts/Either';
import { EitherAsync } from 'purify-ts/EitherAsync';
import * as limiter from '@codegrade/limiter';
import { Cache } from './cache';
import { coerceToError } from './errors';
/**
 * Resolve all EitherAsyncs similar to `EitherAsync.all`.
 *
 * The different between this and `EitherAsync.all` is:
 * 1. It has the correct return type if you pass in a tuple.
 * 2. It allows you to limit the max concurrency.
 *
 * If you just want to run a few EitherAsyncs at the same time, take a look at `every`.
 *
 * @param arr The EitherAsyncs to run.
 * @param opts The options for running them.
 * @returns An EitherAsync that resolves to a right if all `EitherAsync`s in the array resolve to
 * a `Right`, or otherwise a left with the frist EitherAsync in `arr` that resolved to a left.
 */
export function all(arr, opts = { maxConcurrency: Maybe.empty() }) {
    return EitherAsync(async (helpers) => {
        const res = [];
        const waiter = opts.maxConcurrency
            // Only actually limit if the limit will do anything.
            .filter(max => max < arr.length)
            .caseOf({
            Nothing: () => Promise.all(arr),
            Just: maxConcurrency => {
                const run = limiter.limit((el) => el.run(), {
                    maxConcurrency,
                });
                return Promise.all(arr.map(run));
            },
        });
        for (const e of await waiter) {
            if (e.isLeft()) {
                // eslint-disable-next-line no-await-in-loop
                return await helpers.liftEither(e);
            }
            res.push(e.extract());
        }
        return await helpers.liftEither(Right(res));
    });
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function every(...arr) {
    return all(arr);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function leftFromValue(value) {
    return EitherAsync.liftEither(Left(value));
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function rightFromValue(value) {
    return EitherAsync.liftEither(Right(value));
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function sleep(time) {
    return EitherAsync.fromPromise(() => new Promise(resolve => {
        setTimeout(() => {
            resolve(Right(undefined));
        }, time.asMilliseconds());
    }));
}
/**
 * Perform an EitherAsync but make sure the execution takes at least `time`.
 *
 * @param time The minimum time the EitherAsync should take.
 * @param eitherAsync The EitherAsync to actually execute and return the value of.
 * @returns A fresh `EitherAsync` that will take at least `time` to run.
 */
export function waitAtLeast(time, eitherAsync) {
    return every(eitherAsync, sleep(time)).map(([a]) => a);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function withValue(next, ...prev) {
    return next.map(val => [val, ...prev]);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function timeout(either, timeoutTime) {
    return MaybeAsync.fromPromise(() => new Promise(resolve => {
        let timedOut = false;
        let done = false;
        const cancelTimeoutToken = setTimeout(() => {
            if (!done) {
                timedOut = true;
                resolve(Maybe.empty());
            }
        }, timeoutTime.asMilliseconds());
        void either.then(result => {
            if (!timedOut) {
                done = true;
                clearTimeout(cancelTimeoutToken);
                resolve(Maybe.of(result));
            }
        });
    }));
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function liftEitherFunction(callback) {
    return (...args) => EitherAsync.liftEither(callback(...args));
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function innerIgnoreError(toIgnore, replacementValue, err) {
    if (err instanceof toIgnore) {
        return rightFromValue(replacementValue);
    }
    return leftFromValue(err);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function ignoreError(toIgnore, replacementValue, err) {
    switch (arguments.length) {
        case 0:
            return ignoreError;
        case 1:
            return (realReplacementValue, realErr) => innerIgnoreError(toIgnore, realReplacementValue, realErr);
        case 2:
            return (realErr) => innerIgnoreError(toIgnore, replacementValue, realErr);
        default:
            return innerIgnoreError(toIgnore, replacementValue, err);
    }
}
/**
 * Make an EitherAsync eager.
 *
 * By default all `EitherAsync`s are lazy. This function takes an EitherAsync,
 * starts it and returns an new EitherAsync that wraps the started promise.
 *
 * @param base The base EitherAsync that should be made eager.
 * @returns A new EitherAsync which internally is already started.
 */
export function eager(base) {
    let inner = base.run();
    return EitherAsync(({ fromPromise }) => {
        if (inner == null) {
            inner = base.run();
        }
        const res = fromPromise(inner);
        inner = undefined;
        return res;
    });
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class ImportLoadError extends Error {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(msg) {
        super(msg);
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        this.tag = '$ImportLoadError$';
        Object.freeze(this);
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function importToEitherAsync(promiseImport, libName) {
    return EitherAsync.fromPromise(() => promiseImport.then(lib => {
        if (lib == null) {
            return Left(new ImportLoadError(`Imported library ${libName} is nullish`));
        }
        return Right(lib);
    }, (err) => {
        if (err instanceof Error) {
            return Left(new ImportLoadError(err.message));
        }
        if (typeof err === 'string') {
            return Left(new ImportLoadError(err));
        }
        return Left(new ImportLoadError(`Unknown error happened loading ${libName}: ${err}`));
    }));
}
/**
 * Make an `EitherAsync` retry itself when it fails.
 *
 * @param eitherAsync The `EitherAsync` to run and retry when it fails.
 * @param opts Retry options.
 * @returns An `EitherAsync` of the same type as the passed in one, but when it
 *     errors for the first `opts.maxTries - 1` times it is ran, it is retried
 *     after a delay.
 */
export function makeRetrying(eitherAsync, opts) {
    return EitherAsync(async ({ fromPromise, liftEither }) => {
        let result = await eitherAsync.run();
        let nthTry = 1;
        while (result.isLeft() && opts.shouldRetry(nthTry, result.extract())) {
            const jitter = 1 + opts.jitterFactor * (2 * Math.random() - 1);
            const backoff = opts.backoffFactor ** (nthTry - 1);
            const delay = duration(opts.initialDelay.asMilliseconds() * jitter * backoff, 'ms');
            // eslint-disable-next-line no-await-in-loop
            await fromPromise(sleep(duration(opts.fixupDelay(delay, nthTry))));
            // eslint-disable-next-line no-await-in-loop
            result = await eitherAsync.run();
            nthTry += 1;
        }
        return await liftEither(result);
    });
}
/**
 * Call a function returning an `EitherAsync` on the contents of a `Maybe` and wrap the result again.
 *
 * @param chainer Function to call on the value in the `Maybe`.
 * @returns A function that accepts a `Maybe` and runs `chainer` on its
 *     contents, if any, and finally wraps the result in a `Just` again.
 */
export function maybeChainer(chainer) {
    return maybeVal => maybeVal
        .map(val => chainer(val).map(Maybe.of))
        .orDefaultLazy(() => rightFromValue(Maybe.empty()));
}
/**
 * Cache the result of an EitherAsync.
 *
 * By default every time you use an {@link EitherAsync} its result is
 * recomputed, this function memoizes (caches) the result and will only call the
 * underlying EitherAsync once.
 *
 * @param base The underlying {@link EitherAsync} that should be cached.
 * @returns A new EitherAsync that will use the cached result of {@link base} if
 * it is available.
 */
export function cached(base) {
    const cache = new Cache(base.run.bind(base));
    return EitherAsync.fromPromise(() => cache.v);
}
/**
 * Convert an EitherAsync to a promise.
 *
 * @param inner The EitherAsync to convert.
 * @returns A promise that resolves when the EitherAsync resolves to a right, and rejects if it
 * resolves to a left. If the EitehrAsync resolves to a left containing a non `Error` object it is
 * wrapped.
 */
export async function toPromise(inner) {
    const x = await inner.run();
    if (x.isLeft()) {
        throw coerceToError(x.extract());
    }
    return x.unsafeCoerce();
}
/**
 * Wrap a function that returns an `EitherAsync` to return a promise instead.
 *
 * @param func The function to convert.
 * @returns A new function that returns a promise as described in `toPromise`.
 */
export function promisify(func) {
    return (...args) => toPromise(func(...args));
}
